import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchClient } from "@/lib/api-client";

interface UseFetchMyProductsFilters {
  is_published?: boolean;
  category_id?: string;
  quantity_zero?: boolean;
  quantity_below_ten?: boolean;
  quantity_above_zero?: boolean;
}

interface UseFetchMyProductsParams {
  limit?: number;
  search?: string;
  filters?: UseFetchMyProductsFilters;
  enabled?: boolean;
}

/**
 * Infinite-scroll listing of the authenticated vendor's own products.
 * `GET /product/me` is fully paginated server-side; this consumes it
 * page-by-page instead of hardcoding `page: 1` and silently stranding
 * everything past page 1 (same pattern as `useFetchProducts`,
 * `useFetchVendorProducts`).
 */
export const useFetchMyProducts = (params: UseFetchMyProductsParams = {}) => {
  const { limit = 10, search, filters, enabled = true } = params;

  const queryFn = async ({ pageParam = 1 }) => {
    const response = await fetchClient.GET("/product/me", {
      params: {
        query: {
          page: typeof pageParam === "number" ? pageParam : 1,
          limit,
          ...(search && { search }),
          ...filters,
        },
      },
    });

    const error = response.error;
    if (error) throw error;
    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["product", "me", limit, search, filters],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (pagination?.hasNextPage) return pagination.page + 1;
      return undefined;
    },
    enabled,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
    refetch,
  };
};
